import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { z } from 'zod'

import { type Agent, type AgentId, agentsConfig } from '@langmail/agents'
import {
  hasPendingSubscription,
  isSubscriber,
  type SubscriptionFields,
  trialDaysLeft,
} from '@langmail/auth-config/access'
import { languageOptions, useAuth, useTRPC } from '@langmail/shared-ui'

import { Screen } from '@/components/screen'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AccountsList } from '@/features/account/components/accounts-list'
import { AppPasswords } from '@/features/account/components/app-passwords'
import { AppleProfile } from '@/features/account/components/apple-profile'
import { LangmailAddress } from '@/features/account/components/langmail-address'
import { useDeleteAccount } from '@/features/account/hooks/use-delete-account'
import { useProfile } from '@/features/auth/hooks/use-profile'
import { useToast } from '@/hooks/ui/use-toast'

const CONFIRMATION_TEXT: string = 'DELETE'

const formSchema = z.object({
  confirmText: z.string().refine((val) => val === CONFIRMATION_TEXT, {
    message: `You must type "${CONFIRMATION_TEXT}" to confirm`,
  }),
})

// One line of status for the Plan row; the subscribe page has the detail.
function planLabel(user: SubscriptionFields | null): string {
  if (!user) {
    return ''
  }
  if (hasPendingSubscription(user)) {
    return 'Payment pending'
  }
  const daysLeft = trialDaysLeft(user)
  if (daysLeft !== null) {
    return `Trial, ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
  }
  switch (user.subscription_status) {
    case 'active':
      return isSubscriber(user) ? 'Subscribed' : 'Early access'
    case 'past_due':
      return 'Payment past due'
    default:
      return 'No active plan'
  }
}

export function Account() {
  const { user } = useAuth()
  const { profile, updateProfile } = useProfile()
  const { toast } = useToast()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const deleteAccountMutation = useDeleteAccount()
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      confirmText: '',
    },
  })

  const { data: agentStates } = useQuery(trpc.agents.list.queryOptions())

  // Create map for O(1) lookups: agentId -> { config }
  const stateMap = new Map<string, { agentId: string; config: Record<string, any> }>(
    (agentStates || []).map((state) => [state.agentId, state])
  )

  const updateConfigMutation = useMutation({
    ...trpc.agents.updateConfig.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.agents.list.queryKey() })
      toast({ title: 'Settings updated' })
    },
    onError: () => {
      toast({ title: 'Failed to update settings', variant: 'destructive' })
    },
  })

  const updateLanguageMutation = useMutation({
    ...trpc.user.updateLanguage.mutationOptions(),
    onSuccess: (_data, variables) => {
      updateProfile({ language: variables.language })
      toast({
        title: 'Language updated',
        description: 'Your language preference has been saved.',
      })
    },
    onError: (error) => {
      console.error('Error updating language:', error)
      toast({
        title: 'Error',
        description: 'Failed to update language. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const handleLanguageChange = (newLanguage: string) => {
    if (!user) {
      return
    }

    updateLanguageMutation.mutate({ language: newLanguage })
  }

  // Filter agents by option key presence
  const getAgentsWithOption = (optionKey: string) =>
    agentsConfig.agents
      .filter((agent) => agent.options.some((opt) => opt.key === optionKey))
      .map((agentDef) => ({
        agentDef,
        state: stateMap.get(agentDef.id),
      }))

  // Get option value with fallback to default
  const getOptionValue = (
    state: { config: Record<string, any> } | undefined,
    agentDef: Agent,
    optionKey: string
  ): boolean => {
    const option = agentDef.options.find((opt) => opt.key === optionKey)
    if (!option) {
      return false
    }

    const config = state?.config || {}
    return (config[optionKey] ?? option.default) as boolean
  }

  // Generic option change handler
  const handleOptionChange = (agentId: AgentId, optionKey: string, value: boolean) => {
    const state = stateMap.get(agentId)
    if (!state) {
      return
    }

    const currentConfig = state.config || {}
    updateConfigMutation.mutate({
      agentId,
      config: { ...currentConfig, [optionKey]: value },
    })
  }

  const agentsWithSummary = getAgentsWithOption('includeInSummary')

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (values.confirmText !== CONFIRMATION_TEXT) {
      return
    }

    await deleteAccountMutation.mutateAsync({})
    setShowDeleteDialog(false)
    form.reset()
  }

  return (
    <Screen title="Account">
      <div>
        {/* Langmail Address Section */}
        <LangmailAddress />

        {/* Mail-client app passwords */}
        <AppPasswords />

        {/* iOS setup profile */}
        <AppleProfile />

        {/* Summary Language Section */}
        <div className="mb-8">
          <div className="mb-2 block font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
            Summary language
          </div>
          <Select disabled={!profile} onValueChange={handleLanguageChange} value={profile?.language || 'en'}>
            <SelectTrigger>
              <SelectValue placeholder="Select your language" />
            </SelectTrigger>
            <SelectContent>
              {languageOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Include in Morning Brief Section */}
        {agentsWithSummary.length > 0 && (
          <div className="mb-8">
            <div className="mb-2 block font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
              Include in Morning Brief
            </div>
            <p className="mb-5 text-muted-foreground text-sm italic">
              Emails needing your attention are always shown first.
            </p>
            <div className="space-y-3">
              {/* Static To-Do item - always enabled */}
              <div className="flex items-center gap-3">
                <Checkbox checked={true} className="size-5" disabled={true} id="todo-include-summary" />
                <label className="cursor-default text-[15px] text-muted-foreground" htmlFor="todo-include-summary">
                  To Do
                </label>
              </div>

              {agentsWithSummary.map(({ agentDef, state }) => (
                <div className="flex items-center gap-3" key={agentDef.id}>
                  <Checkbox
                    checked={getOptionValue(state, agentDef, 'includeInSummary')}
                    className="size-5"
                    disabled={!state || updateConfigMutation.isPending}
                    id={`${agentDef.id}-include-summary`}
                    onCheckedChange={(checked) =>
                      handleOptionChange(agentDef.id, 'includeInSummary', checked as boolean)
                    }
                  />
                  <label className="cursor-pointer text-[15px]" htmlFor={`${agentDef.id}-include-summary`}>
                    {agentDef.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Connected Accounts Section */}
        <div className="mb-8">
          <AccountsList />
        </div>

        {/* Plan */}
        <div className="mb-8">
          <p className="mb-1 font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
            Plan
          </p>
          <div className="flex items-center justify-between">
            <p className="text-[15px]">{planLabel(user)}</p>
            <Link className="text-primary text-sm hover:underline" to="/subscribe">
              Manage plan
            </Link>
          </div>
        </div>

        {/* Divider */}
        <hr className="my-8 border-border border-t" />

        {/* Delete Data Button */}
        <AlertDialog onOpenChange={setShowDeleteDialog} open={showDeleteDialog}>
          <AlertDialogTrigger asChild>
            <button className="text-destructive text-sm hover:underline" type="button">
              Delete my data
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Account</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your account and remove all your data from
                our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <Form {...form}>
              <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                  control={form.control}
                  name="confirmText"
                  render={({ field }) => (
                    <FormItem>
                      <FormDescription>
                        Type <span className="font-semibold">{CONFIRMATION_TEXT}</span> to confirm
                      </FormDescription>
                      <FormControl>
                        <Input placeholder={CONFIRMATION_TEXT} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={deleteAccountMutation.isPending}
                    onClick={() => {
                      setShowDeleteDialog(false)
                      form.reset()
                    }}
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={deleteAccountMutation.isPending}
                    type="submit"
                  >
                    {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </form>
            </Form>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Screen>
  )
}
